import React, { useState } from 'react';
import { Loader2, Server, Eye, EyeOff, Trash2, Package, FlaskConical, Save, Settings } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/api/trpc';
import { DateDisplay } from '@/components/date-display';
import { MountPointCard } from './mount-point-card';
import { useLocation } from 'react-router-dom';
import { MigrateMountDialog } from './migrate-mount-dialog';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

interface MountPointManagerProps {
  id: string;
}

export function MountPointManager({ id }: MountPointManagerProps) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const location = useLocation();
  const { storeRoutes, navigate: appNavigate } = useStoreNavigate();
  const [showCDN, setShowCDN] = useState(false);
  const [showOrphans, setShowOrphans] = useState(false);
  const [showUncommitted, setShowUncommitted] = useState(true); // Show by default
  const [showDirectInstances, setShowDirectInstances] = useState(false); // Hide direct instance routes by default
  const [instanceToRemove, setInstanceToRemove] = useState<string | null>(null);
  const [instanceToPromote, setInstanceToPromote] = useState<string | null>(null);
  const [newConfigName, setNewConfigName] = useState('');
  const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);

  const {
    data: mountPointsData,
    isLoading,
    error: mountPointsError,
  } = trpc.plugins.listMountPoints.useQuery(
    {
      storeId: id,
      includeDirectInstanceRoutes: showDirectInstances,
    },
    { enabled: !!id },
  );

  // Fetch available instances separately (only when needed)
  const { data: instancesData, error: instancesError } = trpc.plugins.listAvailableInstances.useQuery(
    { storeId: id },
    { enabled: !!id },
  );

  const mountPoints = mountPointsData?.mountPoints || [];
  const availableInstances = instancesData || [];
  const error = mountPointsError || instancesError;

  // Filter mount points based on showCDN toggle
  const filteredMountPoints = showCDN
    ? mountPoints
    : mountPoints.filter((mp: any) => mp.routeType !== 'hostname' || !mp.hostname?.includes('.cdn.'));

  // Find orphaned instances (no routes and can be removed)
  const orphanedInstances = availableInstances.filter((inst: any) => inst.canRemove);

  // Find uncommitted instances (preview instances) and sort by last edited (most recent first)
  const uncommittedInstances = availableInstances
    .filter((inst: any) => inst.isPreview)
    .sort((a: any, b: any) => {
      // Sort by createdAt (most recent first)
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA;
    });

  console.log('availableInstances', availableInstances);

  // Remove instance mutation
  const removeInstanceMutation = trpc.plugins.removeInstance.useMutation({
    onSuccess: () => {
      utils.plugins.listMountPoints.invalidate();
      toast({
        title: 'Success',
        description: 'Instance removed successfully',
      });
      setInstanceToRemove(null);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setInstanceToRemove(null);
    },
  });

  // Promote preview to production mutation
  const promotePreviewMutation = trpc.plugins.promotePreview.useMutation({
    onSuccess: (result) => {
      utils.plugins.listMountPoints.invalidate();
      setInstanceToPromote(null);
      setNewConfigName('');
      toast({
        title: 'Success',
        description: `Preview saved as "${result.configName}"`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const reindexMutation = trpc.plugins.reindexSplitRoutes.useMutation({
    onSuccess: (data: any) => {
      utils.plugins.listMountPoints.invalidate();
      toast({
        title: 'Success',
        description: data.message || 'Split routes reindexed successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleRemoveInstance = (instanceId: string) => {
    removeInstanceMutation.mutate({ instanceId, storeId: id });
  };

  const handleEditUncommittedConfig = (instance: any) => {
    // Since it's already a preview instance (isPreview=true), just open the editor directly
    console.log('[handleEditUncommittedConfig] Opening editor for preview instance:', instance.id);
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    appNavigate(`${storeRoutes.EDITOR(instance.id)}?returnUrl=${returnUrl}`);
  };

  const handlePromotePreview = (instance: any) => {
    // Generate default production name (remove -draft- suffix)
    const currentName = instance.config?.configName || 'default';
    const baseName = currentName.replace(/-draft-.*$/, '');
    setNewConfigName(baseName);
    setInstanceToPromote(instance.id);
  };

  const confirmPromoteInstance = async () => {
    if (!instanceToPromote || !newConfigName.trim()) return;

    await promotePreviewMutation.mutateAsync({
      instanceId: instanceToPromote,
      newConfigName: newConfigName.trim(),
    });
  };

  const handleRemoveAllOrphans = async () => {
    try {
      // Remove all orphaned instances sequentially
      for (const instance of orphanedInstances) {
        await removeInstanceMutation.mutateAsync({ instanceId: instance.id, storeId: id });
      }
      setShowRemoveAllConfirm(false);
      toast({
        title: 'Success',
        description: `Removed ${orphanedInstances.length} unused instance${orphanedInstances.length > 1 ? 's' : ''}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove some instances',
        variant: 'destructive',
      });
      setShowRemoveAllConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex min-h-[400px] items-center justify-center pt-6">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-destructive flex min-h-[200px] items-center justify-center">
            <p>Error loading mount points: {error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const cdnCount = mountPoints.filter(
    (mp: any) => mp.routeType === 'hostname' && mp.hostname?.includes('.cdn.'),
  ).length;

  if (mountPoints.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-muted-foreground flex min-h-[300px] items-center justify-center">
            <div className="text-center">
              <Server className="mx-auto h-12 w-12 opacity-50" />
              <p className="mt-4">No mount points configured</p>
              <p className="text-sm">Deploy and mount a plugin to get started</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toggles for visibility */}
      <div className="flex justify-end gap-2">
        <MigrateMountDialog id={id} mountPoints={mountPoints} />
        {uncommittedInstances.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowUncommitted(!showUncommitted)}
            className="gap-2 text-xs"
          >
            {showUncommitted ? (
              <>
                <EyeOff className="h-3.5 w-3.5" />
                Hide Uncommitted ({uncommittedInstances.length})
              </>
            ) : (
              <>
                <FlaskConical className="h-3.5 w-3.5" />
                Show Uncommitted ({uncommittedInstances.length})
              </>
            )}
          </Button>
        )}
        {orphanedInstances.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowOrphans(!showOrphans)}
            className="gap-2 text-xs"
          >
            {showOrphans ? (
              <>
                <EyeOff className="h-3.5 w-3.5" />
                Hide Unused Instances ({orphanedInstances.length})
              </>
            ) : (
              <>
                <Package className="h-3.5 w-3.5" />
                Show Unused Instances ({orphanedInstances.length})
              </>
            )}
          </Button>
        )}
        {cdnCount > 0 && (
          <Button variant="outline" size="sm" onClick={() => setShowCDN(!showCDN)} className="gap-2 text-xs">
            {showCDN ? (
              <>
                <EyeOff className="h-3.5 w-3.5" />
                Hide Direct Instance Routes ({cdnCount})
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5" />
                Show Direct Instance Routes ({cdnCount})
              </>
            )}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => reindexMutation.mutate({ storeId: id })}
          disabled={reindexMutation.isPending}
          className="gap-2 text-xs"
        >
          <Settings className="h-3.5 w-3.5" />
          🔧 Fix Split Routes
        </Button>
      </div>

      {/* Uncommitted Instances Section (Preview Instances) */}
      {showUncommitted && uncommittedInstances.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FlaskConical className="h-4 w-4" />
              Uncommitted Instances (Preview)
            </CardTitle>
            <p className="text-muted-foreground text-xs">
              These are preview instances with 24h TTL. Use "Commit" to promote them to production.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {uncommittedInstances.map((instance: any) => (
              <div
                key={instance.id}
                className="bg-background flex items-center justify-between rounded-md border p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{instance.pluginId}</span>
                      <Badge variant="secondary" className="h-5 text-xs">
                        v{instance.version}
                      </Badge>
                      <Badge
                        variant="outline"
                        className="h-5 border-orange-300 bg-orange-50 text-xs text-orange-700 dark:border-orange-700 dark:bg-orange-950/30 dark:text-orange-400"
                      >
                        🧪 {instance.config?.configName || 'default'}
                      </Badge>
                      <Badge variant="outline" className="h-5 text-[10px] text-blue-600">
                        Expires in 24h
                      </Badge>
                      <span className="text-muted-foreground text-[10px]">
                        •{' '}
                        <DateDisplay value={instance.createdAt} format="MMM d, HH:mm" />
                      </span>
                    </div>
                    <code className="text-muted-foreground text-xs">{instance.id}</code>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditUncommittedConfig(instance)}
                    className="h-8 gap-1.5"
                  >
                    <Settings className="h-3.5 w-3.5" />
                    Edit Config
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePromotePreview(instance)}
                    className="h-8 gap-1.5 border-green-200 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-800 dark:bg-green-950/30 dark:text-green-400"
                  >
                    <Save className="h-3.5 w-3.5" />
                    Commit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setInstanceToRemove(instance.id)}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive h-8"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Orphaned Instances Section */}
      {showOrphans && orphanedInstances.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Package className="h-4 w-4" />
                  Unused Instances
                </CardTitle>
                <p className="text-muted-foreground text-xs">
                  These instances only have a direct route or no routes and can be safely removed
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowRemoveAllConfirm(true)}
                className="h-8 text-xs"
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Remove All ({orphanedInstances.length})
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {orphanedInstances.map((instance: any) => (
              <div
                key={instance.id}
                className="bg-background flex items-center justify-between rounded-md border p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{instance.pluginId}</span>
                      <Badge variant="secondary" className="h-5 text-xs">
                        v{instance.version}
                      </Badge>
                      <Badge
                        variant={instance.isPreview ? 'outline' : 'outline'}
                        className={`h-5 text-xs ${
                          instance.isPreview
                            ? 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-950/30 dark:text-orange-400'
                            : ''
                        }`}
                      >
                        {instance.isPreview && '🧪 '}
                        {instance.config?.configName || 'default'}
                      </Badge>
                    </div>
                    <code className="text-muted-foreground text-xs">{instance.id}</code>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setInstanceToRemove(instance.id)}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive h-8"
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Remove
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Mount Points */}
      {filteredMountPoints.map((mountPoint: any) => (
        <MountPointCard
          key={mountPoint.routeKey}
          mountPoint={mountPoint}
          id={id}
          availableInstances={availableInstances}
        />
      ))}

      {!showCDN && cdnCount > 0 && filteredMountPoints.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-muted-foreground flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <Server className="mx-auto h-8 w-8 opacity-50" />
                <p className="mt-2 text-sm">All mount points are Direct Instance Routes</p>
                <p className="text-xs">Click "Show Direct Instance Routes" to view them</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Promote Preview Dialog */}
      <Dialog open={!!instanceToPromote} onOpenChange={(open) => !open && setInstanceToPromote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Preview as Production Config</DialogTitle>
            <DialogDescription>
              Promote this preview instance to a permanent production configuration. This will remove the 24h
              TTL and make it available in the switch matrix.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="configName">Production Config Name</Label>
              <Input
                id="configName"
                placeholder="e.g., blue, red, default"
                value={newConfigName}
                onChange={(e) => setNewConfigName(e.target.value)}
                autoFocus
              />
              <p className="text-muted-foreground text-xs">
                Choose a name for this production configuration. It will appear in the switch matrix.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setInstanceToPromote(null)}>
              Cancel
            </Button>
            <Button
              onClick={confirmPromoteInstance}
              disabled={!newConfigName.trim() || promotePreviewMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              {promotePreviewMutation.isPending ? 'Committing...' : 'Commit to Production'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Instance Confirmation Dialog */}
      <AlertDialog open={!!instanceToRemove} onOpenChange={(open) => !open && setInstanceToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Instance?</AlertDialogTitle>
            <AlertDialogDescription>
              This instance only has a direct route (or no routes) and can be safely removed. If it has a
              direct CDN route, it will also be removed. This action cannot be undone.
              <br />
              <br />
              <code className="bg-muted rounded px-2 py-1 text-xs">{instanceToRemove}</code>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => instanceToRemove && handleRemoveInstance(instanceToRemove)}
              className="bg-destructive hover:bg-destructive/90"
            >
              {removeInstanceMutation.isPending ? 'Removing...' : 'Remove Instance'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove All Orphans Confirmation Dialog */}
      <AlertDialog open={showRemoveAllConfirm} onOpenChange={setShowRemoveAllConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove All Unused Instances?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{orphanedInstances.length}</strong> unused instance
              {orphanedInstances.length > 1 ? 's' : ''} and their direct CDN routes. This action cannot be
              undone.
              <br />
              <br />
              <div className="bg-muted/50 max-h-32 space-y-1 overflow-y-auto rounded-md p-2">
                {orphanedInstances.map((instance: any) => (
                  <div key={instance.id} className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">•</span>
                    <code className="font-mono">{instance.id}</code>
                    <span className="text-muted-foreground">
                      ({instance.pluginId} v{instance.version})
                    </span>
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveAllOrphans}
              className="bg-destructive hover:bg-destructive/90"
              disabled={removeInstanceMutation.isPending}
            >
              {removeInstanceMutation.isPending
                ? 'Removing...'
                : `Remove All ${orphanedInstances.length} Instances`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
